/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { SuggestionProfile } from '../../../aiSuggestionsPluginType';
import { SuggestionAvatar } from '../SuggestionAvatar';

// `Avatar` always draws a `space.025` margin around its own content (so avatars can sit flush in a
// group). Centering the agent avatar within `stack`'s exact `space.300` box cancels that margin out,
// so the visible hex icon — not `Avatar`'s larger margin box — lines up with `stack`'s edges.
const styles = cssMap({
	stack: {
		// Block-level flex, not inline-flex — `Tooltip` wraps this in a plain `div`, and an
		// inline-level child there would sit in a line box whose inherited `line-height` adds
		// leading above the hex avatar, clipping its top. See `SuggestionCard`'s `privateMetadata`.
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: token('space.300'),
		width: token('space.300'),
	},
	// No built-in `Avatar` size is 12px. `small` (24px) scaled by exactly half avoids the blur a
	// less even ratio (e.g. `xxsmall` scaled by 0.75) introduced.
	userBadge: {
		position: 'absolute',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- offsetting `Avatar`'s fixed `space.025` margin, not a design value
		insetBlockEnd: `calc(-1 * ${token('space.025')})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- offsetting `Avatar`'s fixed `space.025` margin, not a design value
		insetInlineEnd: `calc(-1 * ${token('space.025')})`,
		display: 'flex',
		transform: 'scale(0.5)',
		// `Avatar` always draws a `space.025` margin around its own content, so the anchor for the
		// scale — and the inset above — both offset that margin, keeping the visible avatar (not
		// its margin box) flush with `stack`'s bottom-right corner.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- offsetting `Avatar`'s fixed `space.025` margin, not a design value
		transformOrigin: `calc(100% - ${token('space.025')}) calc(100% - ${token('space.025')})`,
	},
});

function AuthorsTooltip({
	agentName,
	children,
	userName,
}: {
	agentName: string;
	children: React.ReactElement;
	userName?: string;
}): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={
				userName
					? formatMessage(aiSuggestionsMessages.cardAuthorsTooltipLabel, { agentName, userName })
					: agentName
			}
			ignoreTooltipPointerEvents
			position="bottom"
		>
			{children}
		</Tooltip>
	);
}

/** Renders the agent and user identities associated with a suggestion. */
export function SuggestionAgentIdentity({
	agent,
	user,
}: {
	agent?: SuggestionProfile | null;
	user?: SuggestionProfile | null;
}): React.JSX.Element {
	const { formatMessage } = useIntl();
	const agentName = agent?.name?.trim();
	const userName = user?.name?.trim();
	const validAgent =
		agent?.accountId && agentName
			? {
					accountId: agent.accountId,
					name: agentName,
					picture: agent.picture?.trim() || undefined,
				}
			: undefined;
	const validUser =
		user?.accountId && userName
			? {
					name: userName,
					picture: user.picture?.trim() || undefined,
				}
			: undefined;

	const agentSlot = <SuggestionAvatar profile={validAgent} size="small" hasBorder={false} />;

	const authorAvatars = (
		<div css={styles.stack}>
			{agentSlot}
			{validUser && (
				<span css={styles.userBadge} data-testid="suggestion-agent-identity-user-badge">
					<Avatar
						label={validUser.name}
						name={validUser.name}
						size="small"
						src={validUser.picture}
					/>
				</span>
			)}
		</div>
	);

	return (
		<AuthorsTooltip
			agentName={validAgent?.name ?? formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)}
			userName={validUser?.name}
		>
			{authorAvatars}
		</AuthorsTooltip>
	);
}
